import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search, ChevronDown, Plus } from 'lucide-react'
import { Popover } from '@base-ui/react/popover'

import { Button } from '@/shared/components/ui/button'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'
import { cn } from '@/shared/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ResourceOption {
  uuid: string
  name: string
  /** Extra info for disambiguation (e.g. "location · city · tier"). */
  secondary?: string
  /** Badge text rendered right-aligned (e.g. tier label). */
  badge?: string
  /** Whether this is a parent / headquarters with sub-items. */
  isParent?: boolean
  /**
   * Raw source record for the option (e.g. the full partnership row). Carried
   * so a consumer can read extra fields off the selected option - notably to
   * pre-fill sibling form fields via an ENTITY_SEARCH `prefill_map`. Opaque to
   * this component; nothing here renders it.
   */
  data?: Record<string, unknown>
}

export interface ResourceSearchInputProps {
  /** Paint the error border. Matches PhoneInput / DatePicker. */
  hasError?: boolean
  /** Currently selected resource - null means nothing is selected. */
  value: { uuid: string; name: string } | null
  /** Called when the user selects or clears the resource. */
  onChange: (value: { uuid: string; name: string } | null) => void
  /** Full list of available options (pre-fetched). Filtered client-side. */
  results?: ResourceOption[]
  /** Called when the search term changes (parent manages the API call). */
  onSearch?: (term: string) => void
  /** True on the parent query's first load (no results cached yet). */
  isLoading?: boolean
  /** True while a parent request is in flight (incl. background refetch). */
  isFetching?: boolean
  /** Icon name (lucide) for the selected state and result rows. */
  iconName?: string
  /** Placeholder for the trigger button. */
  searchPlaceholder?: string
  /** Message when filter returns no results. */
  emptyMessage?: string
  /** Label for the parent badge (e.g. "Headquarters"). */
  parentBadgeLabel?: string
  /**
   * A couple of real example names (from the data) shown in the empty-state
   * hint to suggest what to type, e.g. `["ProEdge", "DecoRidge"]`.
   */
  examples?: string[]
  /**
   * When true, the user can add a not-yet-listed entry by typing its name: an
   * "Add '<name>'" row appears and yields `{ uuid: '', name }` on select. An
   * empty `uuid` marks a free-text (non-member) entry - the caller decides how
   * to persist it. Off by default (pure select-from-results).
   */
  allowCreate?: boolean
  /** Optional class name. */
  className?: string
  /**
   * Custom trigger content (e.g. a "+ Add" label + icon) rendered INSIDE the
   * popover trigger button instead of the default combobox box. The
   * dropdown/search list is unchanged - this only swaps what the user clicks
   * to open it. Rendered as children of `Popover.Trigger` (a native button),
   * not via a render prop, so the open behaviour is robust.
   */
  triggerChildren?: ReactNode
  /** Class name for the custom trigger button (used with triggerChildren). */
  triggerClassName?: string
  /** data-testid for the custom trigger button. */
  triggerTestId?: string
  /** Placeholder for the in-dropdown filter input (defaults to "Filter..."). */
  filterPlaceholder?: string
  /** Override the popup width classes (defaults to matching the trigger). */
  popupClassName?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ResourceSearchInput({
  value,
  onChange,
  results,
  onSearch,
  isLoading = false,
  isFetching = false,
  iconName = 'building-2',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  parentBadgeLabel = 'Headquarters',
  allowCreate = false,
  className,
  triggerChildren,
  triggerClassName,
  triggerTestId,
  filterPlaceholder = 'Filter...',
  popupClassName,
  hasError = false,
}: ResourceSearchInputProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const filterInputRef = useRef<HTMLInputElement>(null)

  const allOptions = useMemo(() => (results ?? []).filter(Boolean), [results])

  const filtered = useMemo(() => {
    if (!filter.trim()) return allOptions
    const q = filter.toLowerCase()
    return allOptions.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.secondary?.toLowerCase().includes(q),
    )
  }, [allOptions, filter])

  const handleFilterChange = (term: string) => {
    setFilter(term)
    onSearch?.(term)
  }

  const handleSelect = (option: ResourceOption) => {
    onChange({ uuid: option.uuid, name: option.name })
    setFilter('')
    setOpen(false)
  }

  const trimmedFilter = filter.trim()
  // Offer "Add '<name>'" only when creation is enabled, the user has typed
  // something, and no listed option already matches that exact name.
  const canCreate =
    allowCreate &&
    trimmedFilter.length > 0 &&
    !filtered.some((o) => o.name.toLowerCase() === trimmedFilter.toLowerCase())

  const handleCreate = () => {
    onChange({ uuid: '', name: trimmedFilter })
    setFilter('')
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setFilter('')
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      setFilter('')
      onSearch?.('')
    }
  }

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        filterInputRef.current?.focus({ preventScroll: true })
      })
    }
  }, [open])

  return (
    <div className={cn('relative w-full', className)}>
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        {triggerChildren ? (
          <Popover.Trigger className={triggerClassName} data-testid={triggerTestId}>
            {triggerChildren}
          </Popover.Trigger>
        ) : (
          <Popover.Trigger
            className={cn(
              'flex w-full items-center justify-between gap-1.5 rounded-md border border-input bg-[var(--bg)] px-3 py-2 text-left transition-colors hover:border-[var(--rule-strong)]',
              // pr-9 leaves room for the clear button (a sibling, not nested).
              value && 'border-[var(--green-border)] bg-[var(--green-bg-subtle)] pr-9',
              // Error outranks the selected state: it must stay visible.
              hasError && 'border-destructive',
            )}
            aria-invalid={hasError || undefined}
          >
            {value ? (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <LucideIconByName
                  name={iconName}
                  size={14}
                  className="shrink-0 text-[var(--green)]"
                />
                <span className="truncate text-[13px] text-[var(--ink)]">
                  {value.name}
                </span>
              </div>
            ) : (
              <span className="text-[13px] text-[var(--ink-3)]">
                {searchPlaceholder}
              </span>
            )}
            {!value && (
              <ChevronDown className="pointer-events-none size-4 shrink-0 text-[var(--ink-3)]" />
            )}
          </Popover.Trigger>
        )}

        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            sideOffset={4}
            align="start"
            className="isolate z-50"
          >
            <Popover.Popup
              onKeyDown={(e) => e.stopPropagation()}
              className={cn(
                'origin-(--transform-origin) rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                popupClassName ?? 'w-(--anchor-width) min-w-[220px]',
              )}
            >
              <div className="border-b-[0.5px] border-[var(--rule)] p-1.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
                  <input
                    ref={filterInputRef}
                    type="text"
                    value={filter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    placeholder={filterPlaceholder}
                    className="h-7 w-full rounded-md bg-[var(--bg-subtle)] pl-7 pr-2 text-[12px] text-[var(--ink-1)] outline-none placeholder:text-[var(--ink-3)]"
                  />
                </div>
              </div>

              <div className="max-h-[240px] overflow-y-auto p-1">
                {/*
                  Loading gate FIRST: only claim "no results" once a request
                  has settled and the list is genuinely empty. While a fetch is
                  in flight with nothing yet to show, say "Loading..." - never
                  the empty state (see CLAUDE.md empty-state rule).
                */}
                {filtered.length === 0 && (isLoading || isFetching) && (
                  <div className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
                    Loading...
                  </div>
                )}
                {filtered.length === 0 &&
                  !isLoading &&
                  !isFetching &&
                  !canCreate && (
                    <div className="px-2 py-4 text-center text-[12px] text-[var(--ink-3)]">
                      {emptyMessage}
                    </div>
                  )}
                {filtered.map((p) => (
                  <button
                    key={p.uuid}
                    type="button"
                    data-testid="resource-option"
                    data-uuid={p.uuid}
                    onClick={() => handleSelect(p)}
                    className="flex w-full cursor-default items-start gap-2.5 rounded-md px-2 py-2 text-left hover:bg-[var(--bg-subtle)]"
                  >
                    <LucideIconByName
                      name={iconName}
                      size={14}
                      className="mt-0.5 shrink-0 text-[var(--ink-3)]"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-[12px] text-[var(--ink)]">
                          {p.name}
                        </span>
                        {p.isParent && (
                          <span className="shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)]">
                            {parentBadgeLabel}
                          </span>
                        )}
                      </div>
                      {p.secondary && (
                        <span className="truncate text-[10px] text-[var(--ink-3)]">
                          {p.secondary}
                        </span>
                      )}
                    </div>
                    {p.badge && (
                      <span className="ml-auto shrink-0 self-start rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)]">
                        {p.badge}
                      </span>
                    )}
                  </button>
                ))}
                {canCreate && (
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="flex w-full cursor-default items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-[var(--bg-subtle)]"
                  >
                    <Plus size={14} className="shrink-0 text-[var(--green)]" />
                    <span className="truncate text-[12px] text-[var(--ink)]">
                      {t('common.addWithName', 'Add "{{name}}"', {
                        name: trimmedFilter,
                      })}
                    </span>
                  </button>
                )}
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      {/* Rendered as a SIBLING of the trigger (not a child) so we don't nest an
          interactive control inside the Popover trigger button. `onPointerDown`
          swallows the press so it never starts a trigger interaction or steals
          focus; `z-10` keeps it above the trigger. */}
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label="Clear selection"
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={handleClear}
          className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-md text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
